/**
 * Auto-Login Page
 * Handles automatic login when user comes from HubSpot app
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import vizzexLogo from '../../assets/VIZZEX.250.png';
import { authApi, setToken, getToken } from '../../lib/api';

// Debug logging to localStorage (persists across page loads)
function debugLog(step: string, data?: unknown) {
  const logs = JSON.parse(localStorage.getItem('vizzex_debug_log') || '[]');
  logs.push({
    time: new Date().toISOString(),
    step,
    data: data ? JSON.stringify(data) : undefined
  });
  // Keep only last 50 entries
  if (logs.length > 50) logs.shift();
  localStorage.setItem('vizzex_debug_log', JSON.stringify(logs));
}

export default function AutoLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser, isAuthenticated } = useAuth();
  
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    debugLog('AutoLoginPage mounted', { isAuthenticated, hasToken: !!getToken() });
    
    // If already authenticated, redirect to dashboard
    if (isAuthenticated) {
      debugLog('Already authenticated, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
      return;
    }

    const verifyToken = async () => {
      const token = searchParams.get('token');
      debugLog('verifyToken called', { hasToken: !!token, tokenLength: token?.length });
      
      if (!token) {
        debugLog('No token in URL');
        setStatus('error');
        setErrorMessage('No login token provided. Please try again from HubSpot.');
        return;
      }

      try {
        debugLog('Calling authApi.verifyAutoLogin');
        const response = await authApi.verifyAutoLogin(token);
        debugLog('verifyAutoLogin response', { success: response.success, hasToken: !!response.token, error: response.error });
        
        if (response.success && response.token) {
          // Store the session token
          debugLog('Setting token in localStorage');
          setToken(response.token);
          debugLog('Token set, calling refreshUser');
          
          // Refresh auth context to load user
          try {
            await refreshUser();
            debugLog('refreshUser completed', { isAuthenticatedAfter: !!getToken() });
          } catch (refreshError) {
            debugLog('refreshUser error', { error: String(refreshError) });
          }
          
          setStatus('success');
          debugLog('Set status to success, will redirect in 1.5s');
          
          // Redirect to dashboard after a brief success message
          setTimeout(() => {
            debugLog('Redirecting to dashboard now');
            navigate('/dashboard', { replace: true });
          }, 1500);
        } else {
          debugLog('Response not successful', { response });
          setStatus('error');
          setErrorMessage(response.error || 'Login verification failed. Please try again.');
        }
      } catch (error) {
        debugLog('verifyAutoLogin catch error', { error: String(error), message: (error as Error)?.message });
        console.error('Auto-login error:', error);
        setStatus('error');
        setErrorMessage('Unable to complete login. Please try again from HubSpot.');
      }
    };

    verifyToken();
  }, [searchParams, navigate, refreshUser, isAuthenticated]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md animate-slide-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={vizzexLogo} 
            alt="VizzEx Pro" 
            className="w-[250px] mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold gradient-text">VizzEx Pro</h1>
        </div>

        {/* Status Card */}
        <div className="glass rounded-2xl p-8">
          {status === 'verifying' && (
            <div className="text-center">
              <Loader2 className="w-12 h-12 mx-auto text-[var(--color-primary-400)] animate-spin mb-4" />
              <h2 className="text-xl font-semibold mb-2">Signing you in...</h2>
              <p className="text-[var(--text-secondary)]">
                Please wait while we verify your HubSpot account.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-[var(--color-success)] mb-4" />
              <h2 className="text-xl font-semibold mb-2">Welcome!</h2>
              <p className="text-[var(--text-secondary)]">
                Login successful. Redirecting to dashboard...
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-[var(--color-error)] mb-4" />
              <h2 className="text-xl font-semibold mb-2">Login Failed</h2>
              <p className="text-[var(--text-secondary)] mb-6">
                {errorMessage}
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full py-3 px-4 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-600)] hover:from-[var(--color-primary-400)] hover:to-[var(--color-primary-500)] text-white font-medium rounded-lg transition-all duration-200"
                >
                  Go to Login Page
                </button>
                <p className="text-sm text-white/70">
                  Or return to HubSpot and try again.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

